import { massUpdateActions } from 'constants/MassUpdates';
import React, { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import { ChevronDown } from 'lucide-react';
import BulkEditModal from 'components/modules/orders/MassUpdateModal';
import useModal from 'hooks/useModal';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import SessionStore from 'stores/SessionStore';
import { OverrideBillingStatusModal } from 'components/modules/shipments/OverrideBillingStatusModal';

const { UPDATE_ACCOUNTING_REVIEW_DATE, OVERRIDE_BILLING_STATUS } =
  massUpdateActions;

function BulkActionsDropdown({ disabled, selectedOrders = [] }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bulkAction, setBulkAction] = useState('');
  const canChangeBillingStatus =
    SessionStore.isBillingStatusAdmin() ||
    SessionStore.isBillingStatusManager();

  const {
    visible: bulkEditModalVisible,
    show: showBulkEditModal,
    close: closeBulkEditModal,
  } = useModal();

  const {
    visible: overrideBillingStatusModalVisible,
    show: showOverrideBillingStatusModal,
    close: closeOverrideBillingStatusModal,
  } = useModal();

  const handleBulkAction = (bulkAction) => {
    switch (bulkAction) {
      case OVERRIDE_BILLING_STATUS:
        showOverrideBillingStatusModal();
        break;
      case UPDATE_ACCOUNTING_REVIEW_DATE:
      default:
        setBulkAction(bulkAction);
        showBulkEditModal();
        break;
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={`mr-2 ${disabled ? 'disabled' : ''}`}
          >
            {t('ACTIONS')} <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => handleBulkAction(UPDATE_ACCOUNTING_REVIEW_DATE)}
          >
            {`${t('UPDATE')} ${t('ACCOUNTING_REVIEW_DATE')}`}
          </DropdownMenuItem>
          {canChangeBillingStatus && (
            <DropdownMenuItem
              onClick={() => {
                handleBulkAction(OVERRIDE_BILLING_STATUS);
              }}
            >
              {t('OVERRIDE_BILLING_STATUS')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {bulkEditModalVisible && (
        <BulkEditModal
          show
          action={bulkAction}
          onHide={() => {
            closeBulkEditModal();
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['ordersCount'] });
          }}
          orders={selectedOrders}
        />
      )}

      {canChangeBillingStatus && overrideBillingStatusModalVisible && (
        <OverrideBillingStatusModal
          orderIds={selectedOrders.map((order) => order.order_oid)}
          open={overrideBillingStatusModalVisible}
          onHide={closeOverrideBillingStatusModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['ordersCount'] });
          }}
        />
      )}
    </>
  );
}

export default BulkActionsDropdown;
